
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

type ProjectStatus = '已完成' | '進行中';

interface Project {
  title: string;
  description: string;
  status: ProjectStatus;
  tags: string[];
  icon: string;
  color: string;
  link?: string;
  highlights: string[];
}

function StatusBadge({ status }: { status: ProjectStatus }) {
  if (status === '已完成') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full border border-green-200">
        <i className="ri-checkbox-circle-fill text-green-500 text-sm"></i>
        已完成
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full border border-orange-200">
      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse"></span>
      進行中
    </span>
  );
}

export default function SideProjectsPage() {
  const [loaded, setLoaded] = useState(false);
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });

  useEffect(() => {
    setLoaded(true);
  }, []);

  const projects: Project[] = [
    {
      title: 'Tableau 物流追蹤數據分析',
      description: '參與物流追蹤數據分析專案，擔任資料視覺化組成員，以 Tableau 建立互動式 Dashboard，呈現配送效率、延誤分析與路線優化洞察。',
      status: '已完成',
      tags: ['Tableau', 'Data Visualization', '物流分析', 'Dashboard'],
      icon: 'ri-truck-line',
      color: 'from-amber-400 to-orange-500',
      link: 'https://bit.ly/public_tableau_francesshen',
      highlights: [
        '設計互動式物流追蹤儀表板',
        '分析配送延誤模式與關鍵影響因子',
        '視覺化呈現各路線效率趨勢',
      ],
    },
    {
      title: 'PowerBI 全台 113-114 年度犯罪資料分析',
      description: '利用 Power BI 分析全台 113–114 年度公開犯罪資料，探索犯罪類型分佈、時間熱點與地區集中度，製作互動式報表供決策參考。',
      status: '已完成',
      tags: ['Power BI', 'Data Analysis', '公開資料', '犯罪分析'],
      icon: 'ri-bar-chart-box-line',
      color: 'from-rose-400 to-rose-600',
      highlights: [
        '整合全台政府開放犯罪資料集',
        '分析犯罪類型、時段與地區分佈',
        '建立互動式篩選與鑽取報表',
      ],
    },
    {
      title: '台北市房產 AI 分析系統',
      description: '擔任 AI 工程師，參與台北市12行政區房地產分析系統開發，以機器學習模型預測房價趨勢，並提供視覺化查詢介面。',
      status: '已完成',
      tags: ['AI', 'Python', '房地產', 'Machine Learning'],
      icon: 'ri-building-2-line',
      color: 'from-orange-500 to-rose-400',
      link: 'http://tperealestate.franceslaw.tech/',
      highlights: [
        '負責 AI 預測模型開發與整合',
        '分析台北各行政區房價走勢與影響因子',
        '提供線上查詢與視覺化介面',
      ],
    },
    {
      title: '監考系統',
      description: '擔任產品負責人兼專案經理，主導監考系統的需求規劃與開發協調，確保系統功能符合考場作業需求，管理進度與跨組溝通。',
      status: '進行中',
      tags: ['Project Management', '系統開發', '需求分析', '流程設計'],
      icon: 'ri-survey-line',
      color: 'from-amber-500 to-orange-400',
      highlights: [
        '擔任 PM，主導需求訪談與規格定義',
        '協調開發、測試團隊確保里程碑進度',
        '設計考場管理與異常回報作業流程',
      ],
    },
    {
      title: '企業模糊搜尋系統',
      description: '擔任資料庫維護人員，參與企業模糊搜尋系統建置，負責資料庫結構設計、資料清洗與搜尋索引優化，提升查詢準確率與效能。',
      status: '進行中',
      tags: ['SQL', '資料庫設計', '搜尋優化', 'Data Cleaning'],
      icon: 'ri-search-2-line',
      color: 'from-rose-500 to-rose-600',
      highlights: [
        '負責資料庫 Schema 設計與日常維護',
        '執行大量企業資料清洗與標準化',
        '優化模糊搜尋查詢效能與索引策略',
      ],
    },
    {
      title: '智慧法律案件管理系統',
      description: '結合法律實務背景，自主開發微服務架構的法律案件管理平台，整合身份驗證、案件追蹤、文件管理與通知系統，部署於 Kubernetes 容器環境。',
      status: '進行中',
      tags: ['Microservices', 'Docker', 'Kubernetes', 'MySQL', 'Redis'],
      icon: 'ri-scales-3-line',
      color: 'from-rose-500 to-orange-500',
      highlights: [
        '設計 auth / case / doc / notification 四大微服務架構',
        '以 Nginx Ingress 統一流量入口，搭配 Redis 快取提升效能',
        '結合律師事務所實務需求規劃案件流程與權限模型',
      ],
    },
    {
      title: '智能車禍理賠判決分析與預測系統',
      description: '打破法律資訊不對稱——以 ELK Stack 處理海量車禍判決書，搭配 AI MCP（Model Context Protocol），讓民眾或法務人員用自然語言詢問案情，AI 自動調用 Elasticsearch 數據給出具備數據支撐的理賠預估。',
      status: '進行中',
      tags: ['ELK Stack', 'Python', 'AI MCP', 'Elasticsearch', 'NLP'],
      icon: 'ri-hammer-line',
      color: 'from-amber-500 to-rose-500',
      highlights: [
        'Python 爬蟲蒐集司法院車禍判決，Logstash ETL 提取賠償金額、過失比例等關鍵欄位',
        'Kibana 儀表板視覺化各傷勢平均慰撫金、歷年判賠趨勢與地院差異熱力圖',
        '開發 AI MCP Server，封裝 ES 查詢工具，讓 AI 以自然語言給出數據支撐的理賠預估',
      ],
    },
    {
      title: '個人履歷作品集網站',
      description: '使用 React + TypeScript + Tailwind CSS 打造的個人作品集網站，展示工作經歷、技能與個人專案，具備多頁路由、滾動動畫與聯絡表單。',
      status: '進行中',
      tags: ['React', 'TypeScript', 'Tailwind CSS', 'Vite'],
      icon: 'ri-window-line',
      color: 'from-orange-400 to-rose-500',
      highlights: [
        '多頁面 SPA 架構，包含 React Router 導覽',
        '滾動觸發動畫與技能百分比進度條',
        '串接 Web3Forms 實現聯絡表單收信',
      ],
    },
  ];

  const completed = projects.filter(p => p.status === '已完成').length;
  const inProgress = projects.filter(p => p.status === '進行中').length;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className={`inline-block px-4 py-2 bg-orange-100 rounded-full mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-sm font-semibold text-orange-700">個人專案</span>
          </div>
          <h1
            className={`text-4xl md:text-6xl font-bold text-gray-900 mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '100ms' }}
          >
            我的<br />個人專案
          </h1>
          <p
            className={`text-lg text-gray-600 max-w-2xl mx-auto mb-10 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            結合法律背景與數據技能，持續透過實作專案深化轉型歷程
          </p>

          {/* Stats */}
          <div className={`flex flex-wrap justify-center gap-4 md:gap-8 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '300ms' }}>
            <div className="flex items-center gap-2 px-5 py-3 bg-white rounded-2xl shadow-md">
              <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
              <span className="text-lg font-bold text-gray-900">{completed}</span>
              <span className="text-sm text-gray-500">已完成</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 bg-white rounded-2xl shadow-md">
              <span className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></span>
              <span className="text-lg font-bold text-gray-900">{inProgress}</span>
              <span className="text-sm text-gray-500">進行中</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 bg-white rounded-2xl shadow-md">
              <i className="ri-folder-open-line text-orange-500 text-xl"></i>
              <span className="text-lg font-bold text-gray-900">{projects.length}</span>
              <span className="text-sm text-gray-500">專案總數</span>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white" ref={gridRef}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-500 overflow-hidden flex flex-col ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Status badge — top right */}
                <div className="absolute top-4 right-4 z-10">
                  <StatusBadge status={project.status} />
                </div>

                {/* Color bar top */}
                <div className={`h-1.5 w-full bg-gradient-to-r ${project.color}`}></div>

                <div className="p-7 flex flex-col flex-grow">
                  {/* Icon + Title */}
                  <div className="flex items-start gap-4 mb-4 pr-20">
                    <div className={`w-12 h-12 bg-gradient-to-br ${project.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <i className={`${project.icon} text-white text-xl`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 leading-snug" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {project.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 leading-relaxed mb-5">
                    {project.description}
                  </p>

                  {/* Highlights */}
                  <ul className="space-y-2 mb-5 flex-grow">
                    {project.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        {h}
                      </li>
                    ))}
                  </ul>

                  {/* Tags + Link */}
                  <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, i) => (
                        <span key={i} className="px-2.5 py-1 bg-orange-50 text-orange-700 text-xs font-semibold rounded-lg border border-orange-100">
                          {tag}
                        </span>
                      ))}
                    </div>
                    {project.link && (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors duration-200 flex-shrink-0"
                      >
                        <i className="ri-external-link-line text-sm"></i>
                        開啟連結
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}
